<template>
  <v-container id="eval-deviceslist">
    <v-row class="d-print-none">
      <v-col cols="12">
        <v-card flat color="transparent">
          <v-card-title class="px-0">
            <h1 class="headline">{{ headline }}</h1>
            <v-spacer></v-spacer>
            <v-btn icon @click="printing()">
              <v-icon>mdi-printer</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-subtitle class="px-0 pb-2">
            Zeitraum: {{ selectedStartDate.format('LL') }} bis {{ selectedEndDate.format('LL') }}
          </v-card-subtitle>

          <v-row class="mb-4">
            <v-col cols="12" sm="6">
              <TextFieldDatePicker
                v-model="startDate"
                label="Von"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <TextFieldDatePicker
                v-model="endDate"
                label="Bis"
              />
            </v-col>
          </v-row>

          <v-alert
            v-if="!$route.params.deviceClass"
            border="left"
            colored-border
            type="info"
            elevation="2"
            class="mt-4"
          >
            <div v-for="[deviceClass, line] in Object.entries(aggregateByDeviceClass)" :key="deviceClass" class="mb-1">
              <v-row no-gutters align="center">
                <v-col>
                  <strong>{{ DEVICE_CLASSES[deviceClass] || deviceClass }}:</strong>
                </v-col>
                <v-col class="text-right">
                  {{ line.value | currency(organization.currency || '€', false, false) }}
                  <span class="grey--text text--darken-1 ml-2">({{ line.count }} Zahlung{{ line.count === 1 ? '' : 'en' }})</span>
                </v-col>
              </v-row>
            </div>
          </v-alert>

          <div v-else>
            <v-toolbar flat dense color="transparent" class="px-0">
              <v-toolbar-title class="body-1 grey--text">Anzeige-Optionen:</v-toolbar-title>
              <v-btn-toggle
                v-model="filter"
                mandatory
                multiple
                background-color="transparent"
                borderless
              >
                <v-btn
                  v-for="item in tableClasses"
                  :key="item.key"
                  :value="item.key"
                  small
                  text
                  :color="filter.includes(item.key) ? 'primary' : ''"
                >
                  <v-icon v-if="filter.includes(item.key)" left small>mdi-check</v-icon>
                  {{ item.human }}
                </v-btn>
              </v-btn-toggle>
            </v-toolbar>

            <v-divider class="mb-4"></v-divider>

            <v-skeleton-loader
              v-if="loading"
              type="article, table-thead, table-tbody"
            ></v-skeleton-loader>

            <div v-else>
              <template v-if="filter.includes('month')">
                <h3 class="title mb-3 mt-6">Einnahmen je Monat</h3>
                <v-card
                  v-for="[month, entries] in aggregateMonthSorted"
                  :key="month"
                  class="mb-6"
                  outlined
                >
                  <v-card-title class="subtitle-1 py-2 grey lighten-4">
                    {{ month | mapMonth }}
                  </v-card-title>
                  <v-card-text class="pa-0">
                    <EvalTableInner :entries="entries" :collectionPlans="collectionPlans" />
                  </v-card-text>
                </v-card>
              </template>

              <template v-if="filter.includes('device')">
                <h3 class="title mb-3 mt-6">Einnahmen nach Gerät</h3>
                <v-card
                  v-for="[deviceUrl, entries] in aggregateDeviceSorted"
                  :key="deviceUrl"
                  class="mb-6"
                  outlined
                >
                  <v-card-title class="subtitle-1 py-2 grey lighten-4">
                    {{ deviceNames[deviceUrl] ? deviceNames[deviceUrl] : "Unbekanntes Gerät" }}
                  </v-card-title>
                  <v-card-text class="pa-0">
                    <EvalTableInner :entries="entries" :collectionPlans="collectionPlans" />
                  </v-card-text>
                </v-card>
              </template>

              <template v-if="filter.includes('collection')">
                <h3 class="title mb-3 mt-6">Einnahmen nach Kollektenzweck</h3>
                <v-card outlined>
                  <v-card-text class="pa-0">
                    <EvalTableInner :entries="aggregateByCollectionPlan" :collectionPlans="collectionPlans" />
                  </v-card-text>
                </v-card>
              </template>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Print-only View -->
    <div class="d-none d-print-block">
      <h1>{{ shortOrgaName }}</h1>
      <h2>{{ headline }}</h2>
      <p>Zeitraum: {{ selectedStartDate.format('LL') }} bis {{ selectedEndDate.format('LL') }}</p>

      <div v-if="!$route.params.deviceClass">
        <p v-for="[deviceClass, line] in Object.entries(aggregateByDeviceClass)" :key="deviceClass">
          <b>{{ DEVICE_CLASSES[deviceClass] || deviceClass }}:</b>
          {{ line.value | currency(organization.currency || '€', false, false) }}
          ({{ line.count }} Zahlung{{ line.count === 1 ? '' : 'en' }})
        </p>
      </div>

      <div v-else>
        <template v-if="filter.includes('month')">
          <h3>Einnahmen je Monat</h3>
          <div v-for="[month, entries] in aggregateMonthSorted" :key="month" class="mb-4">
            <h4>{{ month | mapMonth }}</h4>
            <EvalTableInner :entries="entries" :collectionPlans="collectionPlans" />
          </div>
        </template>

        <div class="page-break"></div>

        <template v-if="filter.includes('device')">
          <h3>Einnahmen nach Gerät</h3>
          <div v-for="[deviceUrl, entries] in aggregateDeviceSorted" :key="deviceUrl" class="mb-4">
            <h4>{{ deviceNames[deviceUrl] ? deviceNames[deviceUrl] : "Unbekanntes Gerät" }}</h4>
            <EvalTableInner :entries="entries" :collectionPlans="collectionPlans" />
          </div>
        </template>

        <div class="page-break"></div>

        <template v-if="filter.includes('collection')">
          <h3>Einnahmen nach Kollektenzweck</h3>
          <EvalTableInner :entries="aggregateByCollectionPlan" :collectionPlans="collectionPlans" />
        </template>
      </div>
    </div>

    <v-dialog
      v-model="evaluationDialog"
      transition="dialog-top-transition"
      :fullscreen="evalFullscreen"
      scrollable
      width="44em"
    >
      <v-card elevation="0">
        <v-card-text>
          <EvaluationStats
            :evalCollectionPlan="evalCollectionPlan"
            :key="evaluationDialog"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            @click.stop.prevent="evaluationDialog = false"
            color="primary"
          >
            Schließen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import moment from 'moment'
import { mapGetters, mapState } from "vuex"
import EvaluationStats from '@/components/revenue/EvaluationStats'
import TextFieldDatePicker from "@/components/UI/TextFieldDatePicker"
import { shortenParish } from '@/lib/regex-tools'
import { FETCH_ORGANIZATIONS, } from '@/store/action-types'
import { DEVICE_CLASSES, DEVICE_TYPES } from "@/lib/device-db"
import { loadingStateWrapper } from "@/modules/common/store/tools"
import EvalTableInner from "@/components/evaluation/EvalTableInner"

function updateAggregate (obj, path, value) {
  let target = obj
  for (const p of path) {
    if (!Object.prototype.hasOwnProperty.call(target, p)) {
      target[p] = {}
    }
    target = target[p]
  }
  target.value = (target?.value ?? 0) + value
  target.count = (target?.count ?? 0) + 1
  target.average = target.value / target.count
}

export default {
  name: "eval-deviceslist",
  components: { EvalTableInner, EvaluationStats, TextFieldDatePicker },
  filters: {
    mapMonth (v) {
      return moment(v).format('MMMM YYYY')
    },
  },
  data () {
    return {
      DEVICE_CLASSES,

      paymentList: [],
      collectionPlans: {},
      deviceNames: {},
      evaluationDialog: false,
      evalCollectionPlan: null,
      evalFullscreen: false,
      rigthNow: moment().format('LLLL'),
      startDate: moment().subtract(1, 'year').startOf('month').format('YYYY-MM-DD'),
      endDate: moment().format('YYYY-MM-DD'),
      filter: ['month', 'device', 'collection'],
      tableClasses: [{ key: 'month', human: 'Monat' }, { key: 'device', human: 'Geräte' }, { key: 'collection', human: 'Zweck' }],
    }
  },
  computed: {
    ...mapState(["loading"]),
    ...mapGetters("organization", { organization: "currentOrganization" }),
    headline () {
      return this.$route.params.deviceClass ? DEVICE_CLASSES[this.$route.params.deviceClass] : 'Geräte'
    },
    aggregateByDeviceClass () {
      const retval = {}
      for (const payment of this.paymentList) {
        updateAggregate(retval, [DEVICE_TYPES[payment?.device?.type]?.deviceClass ?? 'Unbekannt'], parseFloat(payment.amount))
      }
      return retval
    },
    aggregateByMonth () {
      const retval = {}
      for (const payment of this.paymentList) {
        updateAggregate(retval, [moment(payment.date).format("YYYY-MM"), payment.collection_plan], parseFloat(payment.amount))
      }
      return retval
    },
    aggregateMonthSorted () {
      return Object.entries(this.aggregateByMonth).sort().reverse()
    },
    aggregateByDevice () {
      const retval = {}
      for (const payment of this.paymentList) {
        updateAggregate(retval, [payment.device.url, payment.collection_plan], parseFloat(payment.amount))
      }
      return retval
    },
    aggregateDeviceSorted () {
      return Object.entries(this.aggregateByDevice).sort(
        ([a, x_], [b, y_]) => this.mapDevice(a).localeCompare(this.mapDevice(b))
      )
    },
    mapDevice (url) {
      return this.deviceNames[url] || "Unbekanntes Gerät"
    },
    aggregateByCollectionPlan () {
      const retval = {}
      for (const payment of this.paymentList) {
        updateAggregate(retval, [payment.collection_plan], parseFloat(payment.amount))
      }
      return retval
    },
    shortOrgaName () {
      return shortenParish(this.organization.name)
    },
    selectedStartDate () {
      const date = moment(this.startDate)
      return date.isValid() ? date : moment().subtract(1, 'year').startOf('month')
    },
    selectedEndDate () {
      const date = moment(this.endDate)
      return date.isValid() ? date : moment()
    }
  },
  created () {},
  watch: {
    '$route.params.deviceClass': function () {
      this.load()
    },
    organization: function () {
      this.load()
    },
    startDate: function (newVal) {
      if (newVal) {
        this.load()
      }
    },
    endDate: function (newVal) {
      if (newVal) {
        this.load()
      }
    },
  },
  methods: {
    openEvaluationDialog (eC) {
      // Taken out to fix errors
      // this.evalCollectionPlan = eC
      // this.evaluationDialog = true
    },
    async load () {
      const startDate = this.selectedStartDate.format('YYYY-MM-DD')

      let startOfMonth = this.selectedEndDate.clone().startOf('month').format('YYYY-MM-DD')
      let endOfMonth = this.selectedEndDate.clone().endOf('month').add(1, 'days').format('YYYY-MM-DD')
      let monthCounter = 0

      const fullPaymentList = []
      const paymentLoadJobs = []

      // Load payments in parallel
      while (moment(startOfMonth) >= moment(startDate)) {
        paymentLoadJobs.push(loadingStateWrapper(this.$store, async () => {
          const response = await this.$store.getters.restApi.get(`${this.organization.url}payments/?recursive=false&since=${startOfMonth}&before=${endOfMonth}&tz=Europe/Berlin`)
          if (response.data.length > 0) {
            fullPaymentList.push(...response.data)
          }
        }))

        monthCounter++
        startOfMonth = this.selectedEndDate.clone().subtract(monthCounter, 'months').clone().startOf('month').format('YYYY-MM-DD')
        endOfMonth = this.selectedEndDate.clone().subtract(monthCounter, 'months').clone().endOf('month').add(1, 'days').format('YYYY-MM-DD')
      }

      await Promise.allSettled(paymentLoadJobs)
      // Make sure to render once before continuing on
      await this.$nextTick()

      this.collectionPlans = {}
      this.paymentList = []
      const relevantPlans = new Set()

      for (const payment of fullPaymentList) {
        const deviceClass = DEVICE_TYPES[payment.device?.type ?? null]?.deviceClass

        if (!this.$route.params.deviceClass || this.$route.params.deviceClass === deviceClass) {
          this.$set(this.deviceNames, payment.device?.url, payment.device?.name)
          this.paymentList.push(payment)
          if (payment.collection_plan) {
            relevantPlans.add(payment.collection_plan)
          }
        }
      }

      await Promise.allSettled(Array.from(relevantPlans).map(planUrl => loadingStateWrapper(
        this.$store,
        async () => {
          const response = await this.$store.getters.restApi.get(`${planUrl}`)
          this.$set(this.collectionPlans, planUrl, response.data)
        }
      )))
    },
    printing () {
      window.print()
    },
  },
  async mounted () {
    await this.$nextTick()
    await Promise.allSettled([
      this.$store.dispatch('organization/' + FETCH_ORGANIZATIONS),
    ])
    await this.load()
  },
}
</script>
<style lang="stylus" scoped>
.firstcol
  padding: 0px!important
#eval-donationslist
  #donationstable
    tr
      cursor: pointer
@media all
  .page-break
    display: none
@media print
  .page-break
    display: block
    page-break-before: always
</style>
